import {
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
} from "@material-ui/core"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import ErrorIcon from "@material-ui/icons/Error"
import PlayArrowIcon from "@material-ui/icons/PlayArrow"
import React, { useEffect, useState } from "react"
import {
    Conexoes,
    executarSimulacao,
    listarPedidosParaTeste,
    obterConexoes,
    PedidoParaTeste,
    ResultadoSimulacao,
} from "../../../services/openDelivery"
import useLoadingStore from "../../../stores/LoadingStore"
import { useIntegracaoStyles } from "../../../utils/integracaoParceirosStyles"
import { useSnackbar } from "../../../utils/notificacoes"

const formatarData = (valor: string | null) => {
    if (!valor) return ""
    const data = new Date(valor)
    return isNaN(data.getTime()) ? "" : data.toLocaleString("pt-BR")
}

const formatarMoeda = (valor: number | null) =>
    valor == null ? "" : valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })

/** Deixa o JSON legível sem quebrar se o parceiro devolver algo que não é JSON. */
const formatarPayload = (payload: string | null) => {
    if (!payload) return ""
    try {
        return JSON.stringify(JSON.parse(payload), null, 2)
    } catch {
        return payload
    }
}

/**
 * Simulador da integração: roda a sequência que um PDV executaria e mostra o resultado de cada
 * passo, para validar uma conexão antes de entregar as credenciais ao parceiro.
 *
 * As chamadas são HTTP de verdade contra a nossa própria API, feitas no backend — assim o teste
 * exercita roteamento, filtro e serialização, e o Client Secret não passa pelo navegador.
 */
export function OpenDeliverySimuladorForm() {
    const classes = useIntegracaoStyles()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)

    const [conexoes, setConexoes] = useState<Conexoes | null>(null)
    const [pedidos, setPedidos] = useState<PedidoParaTeste[]>([])
    const [credencialId, setCredencialId] = useState<number | "">("")
    const [pedidoId, setPedidoId] = useState<number | "">("")
    const [confirmar, setConfirmar] = useState(false)
    const [executando, setExecutando] = useState(false)
    const [resultado, setResultado] = useState<ResultadoSimulacao | null>(null)

    const alertStart = (msg: string, tipo: "success" | "error" | "info" | "warning") =>
        enqueueSnackbar(msg, { variant: tipo })

    useEffect(() => {
        const carregar = async () => {
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            setLoading(true)
            try {
                const [resConexoes, resPedidos] = await Promise.all([
                    obterConexoes(autorizacao),
                    listarPedidosParaTeste(autorizacao),
                ])

                if ("retornoErro" in resConexoes) {
                    alertStart(resConexoes.mensagem || "Erro ao carregar conexões", "error")
                } else {
                    setConexoes(resConexoes)
                    const ativa = resConexoes.credenciais?.find(c => c.ativo)
                    if (ativa) setCredencialId(ativa.id)
                }

                if ("retornoErro" in resPedidos) {
                    alertStart(resPedidos.mensagem || "Erro ao carregar pedidos", "error")
                } else {
                    setPedidos(resPedidos)
                    if (resPedidos.length > 0) setPedidoId(resPedidos[0].id)
                }
            } finally {
                setLoading(false)
            }
        }
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleExecutar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        if (credencialId === "" || pedidoId === "") {
            alertStart("Escolha a conexão e o pedido.", "error")
            return
        }
        setExecutando(true)
        setResultado(null)
        try {
            const res = await executarSimulacao(autorizacao, {
                credencialId: Number(credencialId),
                pedidoId: Number(pedidoId),
                confirmar,
            })
            if ("retornoErro" in res) {
                alertStart(res.mensagem || "Erro ao executar a simulação", "error")
            } else {
                setResultado(res)
                alertStart(
                    res.sucesso
                        ? "Integração funcionando de ponta a ponta."
                        : "A simulação encontrou problemas. Veja os passos abaixo.",
                    res.sucesso ? "success" : "warning"
                )
            }
        } finally {
            setExecutando(false)
        }
    }

    const semConexao = !conexoes || conexoes.credenciais?.length === 0

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Testar Open Delivery
                </Typography>

                <Typography variant="body2" color="textSecondary" paragraph>
                    Executa a mesma sequência que o PDV do parceiro faria — autenticar, buscar
                    eventos, ler o pedido e confirmar o recebimento — e mostra o resultado de cada
                    passo. Use antes de entregar as credenciais ao parceiro.
                </Typography>

                {semConexao ? (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardContent}>
                            <Typography variant="body2" color="textSecondary">
                                Esta loja ainda não tem nenhuma conexão Open Delivery. Conecte um
                                parceiro na tela "Integração Open Delivery" antes de testar.
                            </Typography>
                        </Box>
                    </Paper>
                ) : (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                O que testar
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel>Conexão</InputLabel>
                                        <Select
                                            label="Conexão"
                                            value={credencialId}
                                            onChange={e =>
                                                setCredencialId(Number(e.target.value) || "")
                                            }
                                        >
                                            {conexoes?.credenciais?.map(credencial => (
                                                <MenuItem key={credencial.id} value={credencial.id}>
                                                    {credencial.integradorNome}
                                                    {!credencial.ativo && " (desativada)"}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel>Pedido</InputLabel>
                                        <Select
                                            label="Pedido"
                                            value={pedidoId}
                                            onChange={e =>
                                                setPedidoId(Number(e.target.value) || "")
                                            }
                                        >
                                            {pedidos.map(pedido => (
                                                <MenuItem key={pedido.id} value={pedido.id}>
                                                    {`#${
                                                        pedido.localizador || pedido.id
                                                    } — ${formatarData(
                                                        pedido.dataRealizacao
                                                    )} — ${formatarMoeda(pedido.valorTotal)}`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={confirmar}
                                                onChange={e => setConfirmar(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Incluir o passo de aceite do pedido"
                                    />
                                    <Typography variant="body2" color="error">
                                        Atenção: o aceite{" "}
                                        <strong>altera o status do pedido de verdade</strong>. Os
                                        demais passos não mudam nada — o evento criado para o teste
                                        é confirmado ao final e sai da fila.
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                    className={classes.saveButton}
                                    variant="contained"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={handleExecutar}
                                    disabled={executando}
                                >
                                    {executando ? "Executando..." : "Executar teste"}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {resultado && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                Resultado
                            </Typography>
                            <Box ml={2}>
                                <Chip
                                    size="small"
                                    label={resultado.sucesso ? "tudo certo" : "com falhas"}
                                    color={resultado.sucesso ? "primary" : "secondary"}
                                />
                            </Box>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Typography variant="caption" color="textSecondary">
                                orderId usado no teste: {resultado.orderUuid}
                            </Typography>

                            {resultado.passos.map((passo, indice) => (
                                <Box key={indice} mt={2}>
                                    <Box display="flex" alignItems="center">
                                        {passo.sucesso ? (
                                            <CheckCircleIcon
                                                style={{ color: "#4caf50", marginRight: 8 }}
                                            />
                                        ) : (
                                            <ErrorIcon
                                                style={{ color: "var(--cor-erro)", marginRight: 8 }}
                                            />
                                        )}
                                        <Typography variant="subtitle2">
                                            {indice + 1}. {passo.titulo}
                                        </Typography>
                                        {passo.http != null && (
                                            <Box ml={1}>
                                                <Chip size="small" label={`HTTP ${passo.http}`} />
                                            </Box>
                                        )}
                                    </Box>

                                    <Typography variant="body2" color="textSecondary">
                                        {passo.descricao}
                                    </Typography>

                                    {passo.erro && (
                                        <Typography variant="body2" color="error">
                                            {passo.erro}
                                        </Typography>
                                    )}

                                    {passo.payload && (
                                        <Box
                                            component="pre"
                                            mt={1}
                                            p={1.5}
                                            style={{
                                                backgroundColor: "#f8f9fa",
                                                borderRadius: 8,
                                                fontSize: 12,
                                                maxHeight: 320,
                                                overflow: "auto",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {formatarPayload(passo.payload)}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
